import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";

const Blog = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const getLocalizedContent = (post: any) => {
    const titleKey = `title_${language}` as keyof typeof post;
    const excerptKey = `excerpt_${language}` as keyof typeof post;
    
    return {
      title: post[titleKey] || post.title,
      excerpt: post[excerptKey] || post.excerpt,
    };
  };
  
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const structuredData = posts && posts.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Shikwaru Adventures Blog",
    "description": "Travel tips, destination guides, and stories from the African wilderness",
    "blogPost": posts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "datePublished": post.published_at,
      "image": post.featured_image
    }))
  } : undefined;

  return (
    <div className="min-h-screen">
      <SEO
        title="African Safari Blog | Expert Travel Tips, Wildlife Stories & Destination Guides"
        description="Insider safari advice from 20+ years experience. Compare Southern vs East Africa, best time to visit Okavango Delta, private reserves vs national parks. Wildlife photography tips, packing guides, seasonal travel advice. Updated by professional safari guides."
        keywords="African safari blog, safari travel tips, when to visit Okavango Delta, safari planning guide, private game reserves vs national parks, Southern Africa vs East Africa safaris, wildlife photography tips, safari packing list, best time for safari, Botswana travel blog, Namibia travel guide, safari season advice"
        structuredData={structuredData}
        canonical="/blog"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Safari Blog</h1>
            <p className="text-xl text-muted-foreground">
              Travel tips, destination guides, and stories from the African wilderness
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i}>
                  <Skeleton className="w-full h-64" />
                  <CardHeader>
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => {
                const localizedContent = getLocalizedContent(post);
                return (
                  <Card 
                    key={post.id} 
                    className="overflow-hidden hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <img
                      src={post.featured_image}
                      alt={localizedContent.title}
                      className="w-full h-64 object-cover"
                    />
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-3">
                        {post.category && (
                          <Badge variant="secondary">{post.category}</Badge>
                        )}
                        {post.tags && post.tags.slice(0, 2).map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <CardTitle className="line-clamp-2">{localizedContent.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {localizedContent.excerpt}
                      </CardDescription>
                    </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <User size={14} className="mr-1" />
                        <span>{post.author}</span>
                      </div>
                      {post.published_at && (
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          <span>{format(new Date(post.published_at), "MMM dd, yyyy")}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground mb-4">
                No blog posts yet. Check back soon for exciting safari stories and travel tips!
              </p>
            </div>
          )}
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
